import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import StatCard from '../components/common/StatCard';
import PatientSearch from '../components/common/PatientSearch';
import StartVisitModal from '../components/patient/StartVisitModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { patientApi, visitApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { useClinicEvent } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { formatAge, formatTime, titleCase, toInputDate } from '../utils/format';

const STATUS_META = {
  waiting: { label: 'Waiting', bg: 'secondary', icon: 'bi-hourglass-split' },
  'in-consultation': { label: 'With Doctor', bg: 'warning', icon: 'bi-person-check' },
  completed: { label: 'Done', bg: 'success', icon: 'bi-check-circle' },
  cancelled: { label: 'Cancelled', bg: 'light', icon: 'bi-x-circle' },
};

export default function Queue() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can, isDoctor } = useAuth();

  const [date, setDate] = useState(toInputDate(new Date()));
  const [visits, setVisits] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [startFor, setStartFor] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await visitApi.queue({ date });
      setVisits(res.data);
      setCounts(res.counts);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [date, toast]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // The heart of the two-way sync: anything either desk does reloads this list.
  useClinicEvent(['queue:changed', 'consultation:saved'], load);

  async function pickPatient(patient) {
    // The search box returns a summary; the modal needs allergies and visit count.
    try {
      const res = await patientApi.get(patient._id);
      if (res.data.openVisit) {
        toast.warn(`${res.data.name} is already in today's queue`);
        return;
      }
      setStartFor(res.data);
    } catch (err) {
      toast.error(err);
    }
  }

  async function setStatus(visit, status) {
    try {
      await visitApi.setStatus(visit._id, status);
      if (status === 'in-consultation') navigate(`/consultation/${visit._id}`);
    } catch (err) {
      toast.error(err);
    }
  }

  const isToday = date === toInputDate(new Date());

  return (
    <>
      <PageHeader
        title={isToday ? "Today's Queue" : `Queue for ${date}`}
        subtitle="Updates by themselves when reception or the doctor makes a change"
        icon="bi-people-fill"
      >
        <Form.Control
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ maxWidth: 170 }}
        />
        {can('reception') && (
          <Button className="btn-clinic" onClick={() => navigate('/patients/new')}>
            <i className="bi bi-person-plus me-1" /> New Patient
          </Button>
        )}
      </PageHeader>

      <Row className="g-4 mb-4">
        <Col xs={6} md={3}>
          <StatCard label="Waiting" value={counts.waiting || 0} icon="bi-hourglass-split" color="#d9a213" />
        </Col>
        <Col xs={6} md={3}>
          <StatCard label="With Doctor" value={counts['in-consultation'] || 0} icon="bi-person-check" color="#e0563f" />
        </Col>
        <Col xs={6} md={3}>
          <StatCard label="Completed" value={counts.completed || 0} icon="bi-check-circle" color="#1f9d61" />
        </Col>
        <Col xs={6} md={3}>
          <StatCard
            label="Total Today"
            value={(counts.waiting || 0) + (counts['in-consultation'] || 0) + (counts.completed || 0)}
            icon="bi-people"
            color="#2f4bb5"
          />
        </Col>
      </Row>

      {can('reception') && isToday && (
        <Card className="mb-4">
          <Card.Body>
            <div className="fw-semibold mb-2">
              <i className="bi bi-search me-2" />
              Returning patient? Search by name or mobile number to add them to the queue
            </div>
            <PatientSearch onSelect={pickPatient} />
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            <i className="bi bi-list-ol me-2" />
            Queue
          </span>
          <Button variant="light" size="sm" onClick={load}>
            <i className="bi bi-arrow-clockwise" />
          </Button>
        </Card.Header>

        {loading ? (
          <Loader label="Loading the queue..." />
        ) : visits.length === 0 ? (
          <EmptyState
            icon="bi-clipboard-x"
            title="Nobody in the queue yet"
            hint={isToday ? 'Register a patient or search an existing one to get started.' : 'No visits on this date.'}
          />
        ) : (
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Token</th>
                  <th>Patient</th>
                  <th className="d-none d-lg-table-cell">Reason</th>
                  <th className="d-none d-md-table-cell">Vitals</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th className="text-end" style={{ width: 230 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((v) => {
                  const meta = STATUS_META[v.status];
                  const rowClass = [
                    'queue-row',
                    v.status === 'in-consultation' ? 'is-current' : '',
                    v.status === 'completed' || v.status === 'cancelled' ? 'is-done' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <tr key={v._id} className={rowClass}>
                      <td>
                        <div className="token-badge">{v.tokenNo}</div>
                      </td>

                      <td onClick={() => navigate(`/patients/${v.patient._id}`)}>
                        <div className="fw-semibold">
                          {v.patient.name}
                          {v.visitType === 'new' && (
                            <Badge bg="success" className="ms-2" style={{ fontSize: '0.65rem' }}>
                              NEW
                            </Badge>
                          )}
                        </div>
                        <div className="text-muted small">
                          {v.patient.patientId} &middot; {titleCase(v.patient.gender)}, {formatAge(v.patient)} &middot;{' '}
                          {formatTime(v.visitDate)}
                        </div>
                        {v.patient.allergies?.length > 0 && (
                          <div className="text-danger small">
                            <i className="bi bi-exclamation-triangle-fill me-1" />
                            {v.patient.allergies.join(', ')}
                          </div>
                        )}
                      </td>

                      <td className="d-none d-lg-table-cell small text-muted" style={{ maxWidth: 240 }}>
                        {v.reasonForVisit || '-'}
                      </td>

                      <td className="d-none d-md-table-cell small text-muted">
                        {v.vitals?.bpSystolic ? `BP ${v.vitals.bpSystolic}/${v.vitals.bpDiastolic}` : null}
                        {v.vitals?.temperature ? (
                          <div className={v.vitals.temperature >= 100 ? 'text-danger fw-semibold' : ''}>
                            {v.vitals.temperature} F
                          </div>
                        ) : null}
                        {v.vitals?.pulse ? <div>Pulse {v.vitals.pulse}</div> : null}
                        {!v.vitals?.bpSystolic && !v.vitals?.temperature && !v.vitals?.pulse ? '-' : null}
                      </td>

                      <td>
                        <Badge bg={meta.bg} className={meta.bg === 'light' ? 'text-dark' : ''}>
                          <i className={`bi ${meta.icon} me-1`} />
                          {meta.label}
                        </Badge>
                      </td>

                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end flex-wrap">
                          {isDoctor && v.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              className="btn-clinic"
                              onClick={() =>
                                v.status === 'waiting' ? setStatus(v, 'in-consultation') : navigate(`/consultation/${v._id}`)
                              }
                            >
                              <i className="bi bi-stethoscope me-1" />
                              {v.status === 'waiting' ? 'Start' : v.status === 'completed' ? 'View' : 'Continue'}
                            </Button>
                          )}

                          {v.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => navigate(`/print/${v._id}`)}
                              title="Print the prescription"
                            >
                              <i className="bi bi-printer me-1" /> Print
                            </Button>
                          )}

                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="btn-icon"
                            onClick={() => navigate(`/patients/${v.patient._id}`)}
                            title="Open the full patient record"
                            aria-label="Open the full patient record"
                          >
                            <i className="bi bi-clock-history" />
                          </Button>

                          {can('reception') && v.status === 'waiting' && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="btn-icon"
                              onClick={() => setCancelling(v)}
                              title="Cancel this visit"
                              aria-label="Cancel this visit"
                            >
                              <i className="bi bi-x-lg" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <StartVisitModal show={!!startFor} patient={startFor} onHide={() => setStartFor(null)} />

      <ConfirmModal
        show={!!cancelling}
        title="Cancel this visit?"
        body={
          cancelling ? (
            <>
              Token {cancelling.tokenNo} for <strong>{cancelling.patient.name}</strong> will be removed from the
              queue. The patient record itself stays untouched.
            </>
          ) : null
        }
        confirmLabel="Yes, cancel visit"
        onHide={() => setCancelling(null)}
        onConfirm={async () => {
          try {
            await visitApi.cancel(cancelling._id);
            toast.success('Visit cancelled');
          } catch (err) {
            toast.error(err);
          }
        }}
      />
    </>
  );
}
